/**
 * ScenarioPhaseStepper.tsx
 * FEAT-2026-0007 Phase 6B.2 — segmented step control for the Scenario wizard.
 * Extended by Index Tests Item 5.1 — a third "Index tests" tab.
 * Reordered by Index Tests Item 9.1 (Phase 9, R1 — owner revision 2026-07-23):
 *   the wizard's semantic order is now Phase 1 — Machine planning, Phase 2 —
 *   Index tests, Phase 3 — Employee planning ("index is still part of the
 *   machine; people come last"). The `phase: 1|2|3` numbering and `?phase=`
 *   URL param are unchanged in SHAPE — only what phase 2 vs phase 3 MEAN
 *   flipped. Old deep-links built against the pre-9.1 numbering (`?phase=3`
 *   meaning Index) are an accepted casualty (see plan item 9.1).
 *
 * Shows Phase 1 (Machine planning), Phase 2 (Index tests), and Phase 3
 * (Employee planning) tabs. Phase 1's advance button ("Confirm Machine")
 * passes through Index (phase 2, when enabled) on its way to People — lands
 * on Phase 3 directly when the source has no index tests. Phase 2 (Index)
 * has its own simple back (→1) / forward (→3) buttons since it's a genuine
 * mid-wizard stop, ungated either direction (I20 — independent of the
 * Phase-1 soft gate). Phase 3 (People)'s back button returns to Phase 2 when
 * enabled, else straight to Phase 1.
 *
 * 6B.7 — Guard on advance (Phase 1 → next ONLY):
 *   If any tasks are unplaced (no machine) or hard conflicts remain, a ConfirmDialog
 *   warns the user before proceeding. The advance is soft (user can still
 *   proceed) to avoid hard-blocking progress. A DIRECT click on the People tab
 *   from anywhere mirrors this same soft gate (9.1 — matches the pre-reorder
 *   behavior of clicking the Employee tab), always landing on Phase 3
 *   regardless of Index enablement. A direct click on the Index tab is never
 *   gated (R1).
 *
 * Item 5.1 (I20) — Index tab enablement:
 *   Enabled iff the active source has ≥1 active IndexAllocation row (fetched via
 *   scheduleIndex.ts). Disabled otherwise: greyed out + tooltip. This gate is
 *   INDEPENDENT of the Phase-1 soft gate above — switching to/from the Index
 *   tab never triggers the unplaced/conflict confirm dialog. See
 *   indexTabAvailability.ts for the pure enablement rule (unit-tested
 *   separately).
 *
 * State persistence:
 *   Phase1Panel, IndexTestsPanel, and Phase2Panel all stay mounted at all times;
 *   visibility is toggled via CSS display so each phase's internal state survives
 *   a round-trip through the others.
 *
 * Item 5.4 — the header chrome's `compareToBaseline` toggle (already threaded
 * to Phase1Panel/Phase2Panel) is now also passed to IndexTestsPanel, which
 * gates its own baseline fetch/ghosting on scenario editability (I16).
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Box, Button, Tabs, Tab, Divider, ToggleButton, ToggleButtonGroup, Tooltip, Typography } from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import PrecisionManufacturingIcon from '@mui/icons-material/PrecisionManufacturing';
import GroupsIcon from '@mui/icons-material/Groups';
import ScienceIcon from '@mui/icons-material/Science';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';

import { Phase1Panel } from './Phase1Panel';
import { Phase2Panel } from './Phase2Panel';
import { type Phase2ViewMode, readPhase2ViewMode, writePhase2ViewMode } from './phase2ViewMode';
import { IndexTestsPanel } from './index/IndexTestsPanel';
import { isIndexTabEnabled, INDEX_TAB_DISABLED_TOOLTIP } from './index/indexTabAvailability';
import {
  ConfirmDialog,
  CONFIRM_INITIAL,
  type ConfirmDialogState,
} from '../../../../components/shared/ConfirmDialog';
import type { Scenario } from '../../../../services/scheduleScenario';
import { listIndexAllocations } from '../../../../services/scheduleIndex';
import { onScheduleChange } from '../../../../services/scheduleBus';

/**
 * Tooltip-wrapped Tab that still works as a DIRECT child of <Tabs>.
 *
 * MUI's Tabs wires value/selected/onChange into its immediate children via
 * cloneElement, so a plain <Tooltip><span><Tab/></span></Tooltip> silently
 * disconnects the tab — clicks never reach Tabs.onChange (caught by the
 * item-7.2 browser E2E). This wrapper receives the cloned props and spreads
 * them onto the real Tab; the span keeps pointer events alive so the tooltip
 * still fires while the tab is disabled.
 */
const TooltipTab = ({
  tooltip,
  ...tabProps
}: { tooltip: string } & React.ComponentProps<typeof Tab>) => (
  <Tooltip title={tooltip} arrow disableHoverListener={!tooltip}>
    <Box component="span" sx={{ display: 'inline-flex' }}>
      <Tab {...tabProps} />
    </Box>
  </Tooltip>
);

interface ScenarioPhaseStepperProps {
  phase: 1 | 2 | 3;
  onPhaseChange: (phase: 1 | 2 | 3) => void;
  compareToBaseline: boolean;
  /** True only when a scenario is active; disables advance when false. */
  scenarioActive: boolean;
  /** The active scenario — passed to Phase1Panel for source + scenarioKey context. */
  scenario: Scenario | null;
}

export const ScenarioPhaseStepper: React.FC<ScenarioPhaseStepperProps> = ({
  phase,
  onPhaseChange,
  compareToBaseline,
  scenarioActive,
  scenario,
}) => {
  const theme = useTheme();

  // ── Phase-1 stats (unplaced + hard conflicts) — reported by Phase1Panel (6B.7) ─
  const [phase1Unplaced, setPhase1Unplaced] = useState(0);
  const [phase1HardConflicts, setPhase1HardConflicts] = useState(0);

  const handlePhase1Stats = (unplaced: number, hardConflicts: number) => {
    setPhase1Unplaced(unplaced);
    setPhase1HardConflicts(hardConflicts);
  };

  // ── 6D.2 — On-enter refresh keys ───────────────────────────────────────────
  // Each panel's `onEnterRefreshKey` is bumped whenever that panel becomes the
  // active phase.  We skip the initial render (initialMountRef) so we don't
  // double-trigger the data fetches that each panel fires on its own mount.
  // Keys are numbered by PHASE NUMBER (not by panel identity) — after the 9.1
  // reorder, phase1EnterKey still feeds Phase1Panel, phase2EnterKey now feeds
  // IndexTestsPanel, and phase3EnterKey now feeds Phase2Panel (Employee planning).
  const [phase1EnterKey, setPhase1EnterKey] = useState(0);
  const [phase2EnterKey, setPhase2EnterKey] = useState(0);
  const [phase3EnterKey, setPhase3EnterKey] = useState(0);
  const initialMountRef = useRef(true);

  useEffect(() => {
    if (initialMountRef.current) {
      // First render — both panels are mounting and will load on their own.
      initialMountRef.current = false;
      return;
    }
    if (phase === 1) {
      setPhase1EnterKey((k) => k + 1);
    } else if (phase === 2) {
      setPhase2EnterKey((k) => k + 1);
    } else {
      setPhase3EnterKey((k) => k + 1);
    }
  // `phase` is the only dependency — we want exactly one fire per transition.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase]);

  // ── Item 5.1 (I20) — Index tab enablement ──────────────────────────────────
  // The tab is enabled iff the active source has ≥1 active IndexAllocation row.
  // Refetched whenever the scenario/source changes, and whenever the user
  // re-enters Phase 1 (Machine) or Phase 3 (Employee planning) — a plan
  // re-generate happens from Phase 1's Auto-schedule control, so returning
  // from either surrounding phase is the cheapest reliable signal that new
  // index allocations may now exist. (9.1 — Index itself is now Phase 2 and
  // already re-fetches on its own via the source-key effect below plus the
  // scheduleBus self-heal, so it's excluded from this pair.)
  const [indexAllocationCount, setIndexAllocationCount] = useState<number | null>(null);
  // ANY-kind allocation count (index + effort) — Phase 3's Staff hero can render
  // pooled allocation rows of either kind even with zero machine tasks, so its
  // gate keys off this broader count (index-only fix, 2026-08-04).
  const [anyAllocationCount, setAnyAllocationCount] = useState<number | null>(null);

  const scenarioSourceType = scenario?.SourceType ?? null;
  const scenarioSourceKey =
    scenarioSourceType === 'Quote'
      ? (scenario?.QuoteKey ?? null)
      : scenarioSourceType === 'WorkOrder'
        ? (scenario?.WorkOrderKey ?? null)
        : null;
  const scenarioKey = scenario?.ScenarioKey ?? null;

  const refreshIndexAllocationCount = useCallback(async () => {
    if (!scenarioKey || !scenarioSourceType || !scenarioSourceKey) {
      setIndexAllocationCount(0);
      setAnyAllocationCount(0);
      return;
    }
    try {
      const rows = await listIndexAllocations(scenarioKey, {
        sourceType: scenarioSourceType,
        sourceKey: scenarioSourceKey,
      });
      // 118: effort-kind allocations don't enable the Index tab (they have
      // no capacity surface); they live in the People phase.
      setIndexAllocationCount(rows.filter((r) => (r.Kind ?? 'index') === 'index').length);
      setAnyAllocationCount(rows.length);
    } catch {
      // Fail closed: an errored fetch leaves the tab greyed out rather than
      // guessing it's enabled.
      setIndexAllocationCount(0);
      setAnyAllocationCount(0);
    }
  }, [scenarioKey, scenarioSourceType, scenarioSourceKey]);

  useEffect(() => {
    void refreshIndexAllocationCount();
  }, [refreshIndexAllocationCount]);

  // Self-heal the tab-enablement count on any schedule mutation (scheduleBus):
  // covers the first-ever plan generation and a transiently-failed fetch, which
  // otherwise leave the tab greyed until a phase re-enter or reload.
  useEffect(() => {
    return onScheduleChange(() => {
      void refreshIndexAllocationCount();
    });
  }, [refreshIndexAllocationCount]);

  const phase1EnterKeyRef = useRef(0);
  const phase3EnterKeyRef = useRef(0);
  useEffect(() => {
    if (phase1EnterKey === phase1EnterKeyRef.current && phase3EnterKey === phase3EnterKeyRef.current) {
      return;
    }
    phase1EnterKeyRef.current = phase1EnterKey;
    phase3EnterKeyRef.current = phase3EnterKey;
    void refreshIndexAllocationCount();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase1EnterKey, phase3EnterKey]);

  const indexTabEnabled = isIndexTabEnabled(indexAllocationCount);
  const hasAnyAllocations = (anyAllocationCount ?? 0) > 0;

  // ── Index-only auto-skip (2026-08-04) ──────────────────────────────────────
  // A plan whose source has ONLY index tests generates zero machine tasks, so
  // landing on Phase 1 shows an empty machine-planning page. Once BOTH facts
  // are known (machine-task count reported by Phase1Panel after its first load,
  // allocation count fetched above), advance 1 → 2 exactly once per scenario.
  // Any other resolution (tasks exist, no index tests, or the user already
  // navigated away) permanently disarms the skip, so clicking back to Phase 1
  // never bounces the user out again.
  const [machineTaskCount, setMachineTaskCount] = useState<number | null>(null);
  const handleTasksLoaded = useCallback((total: number) => setMachineTaskCount(total), []);
  const autoSkipDoneRef = useRef(false);
  useEffect(() => {
    // New scenario → re-arm the skip and forget the previous plan's task count.
    setMachineTaskCount(null);
    autoSkipDoneRef.current = false;
  }, [scenarioKey]);
  useEffect(() => {
    if (autoSkipDoneRef.current) return;
    if (phase !== 1) {
      // Deep-linked / already elsewhere — never yank the user between phases.
      autoSkipDoneRef.current = true;
      return;
    }
    if (machineTaskCount === null || indexAllocationCount === null) return; // still loading
    autoSkipDoneRef.current = true;
    if (machineTaskCount === 0 && indexTabEnabled) {
      onPhaseChange(2);
    }
  }, [phase, machineTaskCount, indexAllocationCount, indexTabEnabled, onPhaseChange]);

  // ── Advance guard dialog ────────────────────────────────────────────────────
  const [confirmState, setConfirmState] = useState<ConfirmDialogState>(CONFIRM_INITIAL);

  const closeConfirm = () => setConfirmState(CONFIRM_INITIAL);

  /**
   * Build a human-readable warning message for the advance guard.
   * Returns null if there is nothing to warn about (clean advance).
   */
  const buildGuardMessage = (): string | null => {
    const parts: string[] = [];
    if (phase1Unplaced > 0) {
      parts.push(
        `${phase1Unplaced} task${phase1Unplaced !== 1 ? 's have' : ' has'} no machine assigned`,
      );
    }
    if (phase1HardConflicts > 0) {
      parts.push(
        `${phase1HardConflicts} hard conflict${phase1HardConflicts !== 1 ? 's remain' : ' remains'}`,
      );
    }
    return parts.length > 0 ? parts.join(' and ') + '.' : null;
  };

  /**
   * Shared soft-gate: if Phase-1 (Machine planning) has unplaced tasks or hard
   * conflicts, warn before advancing to `targetPhase`; otherwise advance
   * immediately. Used by BOTH the Phase-1 advance button (target = Index when
   * enabled, else People) and a direct click on the People tab (target =
   * People always — 9.1 mirrors the pre-reorder Employee-tab-click behavior).
   */
  const handleAdvanceClick = (targetPhase: 2 | 3) => {
    const warning = buildGuardMessage();
    if (warning) {
      setConfirmState({
        open: true,
        title: 'Machine planning incomplete',
        message: `${warning} You can still proceed and come back to resolve these, or continue here first.`,
        confirmLabel: 'Proceed anyway',
        confirmColor: 'warning',
        onConfirm: () => onPhaseChange(targetPhase),
      });
    } else {
      onPhaseChange(targetPhase);
    }
  };

  /** Phase-1's advance button: Index (2) when enabled, else straight to People (3). */
  const handleAdvanceFromMachine = () => {
    handleAdvanceClick(indexTabEnabled ? 2 : 3);
  };

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    if (newValue === 0) {
      onPhaseChange(1);
      return;
    }
    if (newValue === 1) {
      // Index tests (I20/R1): direct switch, no confirm gate. The tab is
      // disabled (and thus never reaches onChange) when there are no index
      // allocations, but re-check defensively in case of a stale click.
      if (indexTabEnabled) {
        onPhaseChange(2);
      }
      return;
    }
    // Employee planning (People): mirrors the pre-9.1 behavior of clicking the
    // Employee tab directly — same Phase-1 soft gate, landing on People (3)
    // regardless of whether the Index tab is enabled.
    handleAdvanceClick(3);
  };

  // Phase-3 view (Staff this job / Classic / Board / Roster) — lifted from
  // Phase2Panel (2026-07-26 item 2) so the switcher shares the phase-tab row.
  // Persistence unchanged (phase2ViewMode helpers).
  const [phase2View, setPhase2View] = useState<Phase2ViewMode>(readPhase2ViewMode);
  useEffect(() => { writePhase2ViewMode(phase2View); }, [phase2View]);

  // ── Render ──────────────────────────────────────────────────────────────────

  return (
    <Box>
      {/* ── Phase tabs (segmented control) ────────────────────────────────── */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 2,
          mb: 1,
        }}
      >
        {/* Compact step tabs (designer pass 2026-07-26): a green underline marks
            the active phase instead of the raised pill — same tabs, same gates,
            less chrome. */}
        <Tabs
          value={phase === 1 ? 0 : phase === 2 ? 1 : 2}
          onChange={handleTabChange}
          sx={{
            minHeight: 34,
            '& .MuiTabs-indicator': {
              height: 3,
              borderRadius: '3px 3px 0 0',
              backgroundColor: theme.palette.primary.main,
            },
          }}
        >
          <Tab
            icon={<PrecisionManufacturingIcon sx={{ fontSize: 16 }} />}
            iconPosition="start"
            label="Phase 1 — Machine planning"
            sx={{
              minHeight: 34, py: 0.5, px: 1.5, textTransform: 'none',
              fontSize: '0.8rem', fontWeight: 600, gap: 0.6,
              '&.Mui-selected': { color: theme.palette.primary.main },
              '&:not(.Mui-selected)': { color: alpha(theme.palette.text.primary, 0.55) },
            }}
          />
          <TooltipTab
            tooltip={indexTabEnabled ? '' : INDEX_TAB_DISABLED_TOOLTIP}
            icon={<ScienceIcon sx={{ fontSize: 16 }} />}
            iconPosition="start"
            label="Phase 2 — Index tests"
            disabled={!scenarioActive || !indexTabEnabled}
            sx={{
              minHeight: 34, py: 0.5, px: 1.5, textTransform: 'none',
              fontSize: '0.8rem', fontWeight: 600, gap: 0.6, width: '100%',
              '&.Mui-selected': { color: theme.palette.primary.main },
              '&:not(.Mui-selected)': { color: alpha(theme.palette.text.primary, 0.55) },
            }}
          />
          <Tab
            icon={<GroupsIcon sx={{ fontSize: 16 }} />}
            iconPosition="start"
            label="Phase 3 — Employee planning"
            disabled={!scenarioActive}
            sx={{
              minHeight: 34, py: 0.5, px: 1.5, textTransform: 'none',
              fontSize: '0.8rem', fontWeight: 600, gap: 0.6,
              '&.Mui-selected': { color: theme.palette.primary.main },
              '&:not(.Mui-selected)': { color: alpha(theme.palette.text.primary, 0.55) },
            }}
          />
        </Tabs>

        {/* Phase navigation button(s) — Phase 2 (Index) gets both a back and a
            forward button since it's a genuine mid-wizard stop (9.1); neither
            is gated (I20 — independent of the Phase-1 soft gate). */}
        {phase === 1 ? (
          <Button
            variant="contained"
            size="small"
            endIcon={<ArrowForwardIcon />}
            disabled={!scenarioActive}
            onClick={handleAdvanceFromMachine}
            sx={{ textTransform: 'none', fontWeight: 600, whiteSpace: 'nowrap' }}
          >
            {indexTabEnabled ? 'Confirm Machine → Index Tests' : 'Confirm Machine → Employee Planning'}
          </Button>
        ) : phase === 2 ? (
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              variant="outlined"
              size="small"
              startIcon={<ArrowBackIcon />}
              onClick={() => onPhaseChange(1)}
              sx={{ textTransform: 'none', whiteSpace: 'nowrap' }}
            >
              Back to Machine planning
            </Button>
            <Button
              variant="contained"
              size="small"
              endIcon={<ArrowForwardIcon />}
              onClick={() => onPhaseChange(3)}
              sx={{ textTransform: 'none', fontWeight: 600, whiteSpace: 'nowrap' }}
            >
              Continue to Employee planning
            </Button>
          </Box>
        ) : (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
            {/* Phase-3 view switcher — shares the tab row (2026-07-26 item 2). */}
            <ToggleButtonGroup
              value={phase2View}
              exclusive
              size="small"
              onChange={(_e, v: Phase2ViewMode | null) => { if (v) setPhase2View(v); }}
              aria-label="Employee planning view"
            >
              <ToggleButton value="staff" sx={{ textTransform: 'none', px: 1.3, py: 0.3, fontSize: '0.72rem', fontWeight: 700 }}>
                Staff this job
              </ToggleButton>
              <ToggleButton value="classic" sx={{ textTransform: 'none', px: 1.3, py: 0.3, fontSize: '0.72rem' }}>
                Classic
              </ToggleButton>
              <ToggleButton value="board" sx={{ textTransform: 'none', px: 1.3, py: 0.3, fontSize: '0.72rem' }}>
                Board
              </ToggleButton>
              <ToggleButton value="roster" sx={{ textTransform: 'none', px: 1.3, py: 0.3, fontSize: '0.72rem' }}>
                Roster
              </ToggleButton>
            </ToggleButtonGroup>
            <Button
              variant="outlined"
              size="small"
              startIcon={<ArrowBackIcon />}
              onClick={() => onPhaseChange(indexTabEnabled ? 2 : 1)}
              sx={{ textTransform: 'none', whiteSpace: 'nowrap' }}
            >
              {indexTabEnabled ? 'Back to Index tests' : 'Back to Machine planning'}
            </Button>
          </Box>
        )}
      </Box>

      <Divider />

      {/* ── Phase body — all panels stay mounted for state persistence ─────── */}
      {/* CSS display toggling ensures each phase's internal state survives   */}
      {/* a round-trip through the others.                                    */}

      <Box sx={{ display: phase === 1 ? 'block' : 'none' }}>
        <Phase1Panel
          compareToBaseline={compareToBaseline}
          scenario={scenario}
          onPhase1Stats={handlePhase1Stats}
          onEnterRefreshKey={phase1EnterKey}
          onTasksLoaded={handleTasksLoaded}
          hasIndexTests={indexTabEnabled}
        />
      </Box>

      <Box sx={{ display: phase === 2 ? 'block' : 'none' }}>
        <IndexTestsPanel
          scenario={scenario}
          onEnterRefreshKey={phase2EnterKey}
          compareToBaseline={compareToBaseline}
        />
      </Box>

      <Box sx={{ display: phase === 3 ? 'block' : 'none' }}>
        {/* The "Back to Machine planning" / "Back to Index tests" button above
            returns to the prior phase.                                       */}
        <Phase2Panel
          compareToBaseline={compareToBaseline}
          scenario={scenario}
          onEnterRefreshKey={phase3EnterKey}
          viewMode={phase2View}
          onViewModeChange={setPhase2View}
          hasIndexTests={hasAnyAllocations}
        />
        {phase1Unplaced > 0 && (
          <Box
            sx={{
              mt: 2,
              p: 2,
              borderRadius: 1,
              backgroundColor: alpha(theme.schedule.conflictSoft, 0.10),
              border: `1px solid ${alpha(theme.schedule.conflictSoft, 0.30)}`,
            }}
          >
            <Typography variant="body2" sx={{ color: theme.schedule.conflictSoft, fontWeight: 600 }}>
              Reminder: {phase1Unplaced} task{phase1Unplaced !== 1 ? 's have' : ' has'} no machine
              assigned in Phase 1. Use <em>Back to Machine planning</em> to assign them before
              submitting.
            </Typography>
          </Box>
        )}
      </Box>

      {/* ── Advance guard dialog ────────────────────────────────────────────── */}
      <ConfirmDialog state={confirmState} onClose={closeConfirm} />
    </Box>
  );
};
